// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ClipboardCheck, ShieldAlert, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useGetActivity, useGetDeal, useGetDocuments, useGetTasks, usePatchTask } from "@/hooks/useDeals";
import { useDocumentViewer } from "@/components/deals/documents/DocumentViewer";
import DealSummary from "@/components/deals/DealSummary";
import { PHASE_LABELS } from "@/utils/deals.utils";
import { absoluteUrl, formatUtcDate } from "@/utils/common.utils";
import { nextClientJourneyItems, recentCompletedItems, taskLabel } from "@/utils/deals-view.utils";
import CollabDealSkeleton from "@/components/deals/skeletons/CollabDealSkeleton";
import { Button } from "@/components/ui/button";
import EmptyState from "@/components/common/EmptyState";
import DetailShell from "@/components/layout/DetailShell";
import { COLLAB_ROLE_HEADLINES, DEAL_SIDE_LABELS } from "@/constant/deals.constant";
import PortalAddressBlock from "@/components/deals/portal/PortalAddressBlock";
import PortalActionCard from "@/components/deals/portal/PortalActionCard";
import PortalContactCard from "@/components/deals/portal/PortalContactCard";
import PortalCurrentChapter from "@/components/deals/portal/PortalCurrentChapter";
import RecentActivityCard from "@/components/deals/portal/RecentActivityCard";
import UpcomingAppointmentsCard from "@/components/deals/portal/UpcomingAppointmentsCard";
import PortalDocumentsCard from "@/components/deals/portal/PortalDocumentsCard";
import ConfirmCompleteDialog from "@/components/deals/portal/ConfirmCompleteDialog";
import { RoleTagPill } from "@/components/deals/portal/RoleTagPill";
import { Journey } from "@/components/deals/portal/PortalJourney";
import {
  DelegatedInviteModal,
  EmptyCard,
  ItemRow,
} from "./CollabDealComponents";
import { DealPhase, DealRole, DealType, ParticipantRole, TaskStatus } from "@/enum/common.enum";
import { PUBLIC_ROUTES, SHARED_PROTECTED_ROUTES } from "@/routes/url";

interface CollabDealViewProps {
  dealId?: string | null;
  embedded?: boolean;
}

export default function CollabDealView({ dealId = null, embedded = false }: CollabDealViewProps) {
  const { id: routeDealId } = useParams();
  const id = dealId || routeDealId;
  const { user, context, loading } = useAuth();
  const enabled = Boolean(user) && Boolean(id);
  const { data: deal, isError: dealError, refetch: refetchDeal } = useGetDeal(id, { enabled });
  const { data: tasksData, refetch: refetchTasks } = useGetTasks(id, { enabled });
  const { data: docsData } = useGetDocuments(id, { enabled });
  const { data: activityData } = useGetActivity(id, { enabled });
  const tasks = tasksData?.tasks ?? [];
  const docs = docsData ?? [];
  const activity = activityData ?? [];
  const denied = dealError;
  const [busyTask, setBusyTask] = useState(null);
  const [confirmTask, setConfirmTask] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const { mutate: patchTask, mutateAsync: patchTaskAsync } = usePatchTask();
  const { openDocument: downloadDoc, viewer: documentViewer } = useDocumentViewer();

  const loadAll = () => { refetchDeal(); refetchTasks(); };

  // Guards
  const myParticipation = (context?.deal_participations || []).find(p => p.deal_id === id);
  // Membership alone does not mean this deal is theirs to run — see the twin
  // comment in ClientDealView. An internal user who also holds a participation
  // row on this deal belongs in this shell, not on DealDetail.
  if (!loading && context?.membership && !myParticipation) return <Navigate to={`/deals/${id}`} replace />;
  if (!loading && context && myParticipation && myParticipation.participant_role === ParticipantRole.CLIENT) {
    return <Navigate to={`/me/deal/${id}`} replace />;
  }

  if (denied) return <DetailShell embedded={embedded}><EmptyState icon={ShieldAlert} title="Not assigned to this deal" description="Ask the agent managing the transaction to send you an invitation." className="mx-auto mt-20 max-w-md" /></DetailShell>;
  if (!deal) return <DetailShell embedded={embedded}><CollabDealSkeleton /></DetailShell>;

  const myRole = deal.my_role || "vendor";
  const myTasks = tasks.filter(t => t.assigned_role === myRole);
  const otherTasks = tasks.filter(t => t.assigned_role !== myRole); // shared visible tasks not assigned to me
  const total = myTasks.length || 1;
  const done = myTasks.filter(t => t.status === TaskStatus.COMPLETE).length;
  const pct = Math.round((done / total) * 100);
  // The hero spotlights the single next open item (see `PortalActionCard`).
  // Excluding it from the list below avoids the two-controls-one-job trap the
  // deal page redesign shipped and then had to fix (iteration 62) — the same
  // row would otherwise be checkable from both the hero and the list.
  const myNext = myTasks.find(t => t.status !== TaskStatus.COMPLETE) || null;
  const myTasksBelow = myTasks.filter(t => t.id !== myNext?.id);
  // The timeline reads the viewer's whole visible checklist, same as the
  // client portal's `phaseTasks` — collab tasks aren't phase-gated server-side
  // (externals need forward visibility a phase filter would hide), so `tasks`
  // itself is the right input.
  const journeyUpcoming = nextClientJourneyItems(tasks);
  const journeyCompleted = recentCompletedItems(tasks);
  const agentRoleLabel = deal.deal_type === DealType.SELL ? "Listing Agent" : deal.deal_type === DealType.BUY ? "Buyer's Agent" : "Agent";

  const patchStatus = async (taskId, nextStatus) => {
    const task = tasks.find(t => t.id === taskId);
    setBusyTask(taskId);
    try {
      await patchTaskAsync({ taskId, dealId: id, payload: { status: nextStatus } });
      if (nextStatus === TaskStatus.COMPLETE) {
        toast.success("Marked done. Your agent can see this is off your plate.", {
          action: {
            label: "Undo",
            onClick: () => { patchStatus(taskId, TaskStatus.PENDING).catch(() => undefined); },
          },
        });
      }
    } finally {
      setBusyTask(null);
    }
  };

  // Un-completing stays instant — that direction is already recoverable.
  // Marking complete always goes through the confirm dialog: it notifies the
  // agent and can advance the deal's phase, so it is the one click on this
  // page worth guarding against a fat-finger.
  const requestToggle = (t) => {
    if (t.status === TaskStatus.COMPLETE) {
      patchStatus(t.id, TaskStatus.PENDING).catch(() => undefined);
    } else {
      setConfirmTask(t);
    }
  };

  const confirmCompletion = async () => {
    if (!confirmTask) return;
    await patchStatus(confirmTask.id, TaskStatus.COMPLETE);
    setConfirmTask(null);
  };

  return (
    <DetailShell embedded={embedded}>
      <div className="min-w-0 font-dashboard" data-testid="collab-deal-view">
        {deal.hero_image_url && (
          <img
            src={absoluteUrl(deal.hero_image_url)}
            alt={`Photo of ${deal.address}`}
            className="mb-6 h-48 max-h-[40vh] w-full rounded-2xl object-cover sm:h-64"
            data-testid="collab-hero-image"
          />
        )}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <PortalAddressBlock
            address={deal.address}
            pill={
              <span className="flex-shrink-0 bg-dash-rust text-white text-[10px] font-bold uppercase tracking-[1px] rounded-full px-2.5 py-1">
                {myRole}
              </span>
            }
            overline={COLLAB_ROLE_HEADLINES[myRole] || "Your working view."}
            meta={
              <>
                <span>Client: {deal.client_name} · {PHASE_LABELS[deal.phase]}</span>
                {deal.my_side && deal.my_side !== "neutral" && (
                  <span className="text-[10px] uppercase tracking-[0.2em] bg-moss/10 text-moss px-2 py-0.5" data-testid="collab-side-pill">
                    You&apos;re on the {DEAL_SIDE_LABELS[deal.my_side]}
                  </span>
                )}
                {deal.dual_sided && (
                  <span className="text-[10px] uppercase tracking-[0.2em] bg-bone text-stone px-2 py-0.5">Both sides in room</span>
                )}
                {deal.close_date && <span>· close {formatUtcDate(deal.close_date)}</span>}
              </>
            }
            testId="collab-address"
          />

          <div className="w-full lg:w-fit flex-shrink-0 bg-white border border-dash-border rounded-2xl p-4 flex items-center gap-5" data-testid="collab-progress-widget">
            <div className="flex flex-col gap-0.5">
              <div className="text-[10px] font-bold uppercase tracking-[1px] text-dash-faint">Your progress</div>
              <div className="text-[13px] text-dash-muted">{done} of {myTasks.length} done</div>
            </div>
            <div className="flex-shrink-0 flex items-center justify-center text-dash-ink">
              <span className="font-display text-[36px] font-semibold leading-none">{pct}%</span>
            </div>
          </div>
        </div>

        {/* Delegated invite CTA — co_agent only, under_contract only */}
        {myRole === DealRole.CO_AGENT && deal.phase === DealPhase.UNDER_CONTRACT && (
          <div className="mt-6">
            <Button variant="seamless"
              onClick={() => setInviteOpen(true)}
              className="inline-flex w-full items-center justify-center gap-2 border border-moss px-4 py-2 text-center text-sm text-moss transition-colors hover:bg-moss hover:text-linen sm:w-auto"
              data-testid="coagent-invite-client-btn"
            >
              <UserPlus className="w-4 h-4" /> Invite your client into this room
            </Button>
            <p className="text-xs text-stone mt-2 max-w-md">
              This adds your client to this deal only — never to the host&apos;s workspace. They&apos;ll see the {DEAL_SIDE_LABELS[deal.my_side] || "buyer side"} checklist.
            </p>
          </div>
        )}

        {inviteOpen && (
          <DelegatedInviteModal dealId={id} side={deal.my_side} onClose={() => setInviteOpen(false)} onSent={() => { setInviteOpen(false); loadAll(); }} />
        )}

        {myRole === DealRole.CO_AGENT && (
          <div className="mt-6 rounded-[8px] border border-dash-border bg-white p-5 shadow-[0_10px_24px_rgba(32,35,31,0.06)]" data-testid="coagent-platform-cta">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-dash-muted">Run your side on Stoop</div>
                <h2 className="mt-1 font-display text-xl font-semibold tracking-normal text-dash-ink">Create deal rooms for your own clients.</h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-dash-muted">
                  This view is limited to this transaction. Register your brokerage to open your own deals, invite clients, and manage your side from a full workspace.
                </p>
              </div>
              <Button variant="dashboardPrimary" asChild>
                <Link to={PUBLIC_ROUTES.REGISTER} data-testid="coagent-register-org">Register your org</Link>
              </Button>
            </div>
          </div>
        )}

        {/* Deal summary — contract terms snapshot for co-agents, lenders, attorneys.
            Read-only: `external_focused` already carries `contract_terms` whole,
            and only internal users can PATCH a deal. */}
        {(deal.phase === DealPhase.UNDER_CONTRACT || deal.phase === DealPhase.CLOSED) && (
          <div className="mt-6">
            <DealSummary deal={deal} canEdit={false} />
          </div>
        )}

        <div className="mt-6">
          <PortalActionCard
            tasks={myTasks}
            checklist={tasks}
            team={deal.team || []}
            deal={deal}
            viewerRole={myRole}
            onComplete={(task) => patchStatus(task.id, TaskStatus.COMPLETE)}
            testId="collab-action-items"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-6">
          {/* My responsibilities */}
          <section className="flex flex-col gap-6">
            {myTasksBelow.length > 0 && (
              <div>
                <div className="text-[12px] font-bold uppercase tracking-[1px] text-dash-muted mb-2">My responsibilities</div>
                <div className="bg-white border border-dash-border rounded-2xl p-4 flex flex-col gap-3" data-testid="collab-my-tasks">
                  {myTasksBelow.map((t) => (
                    <ItemRow
                      key={t.id}
                      complete={t.status === TaskStatus.COMPLETE}
                      title={taskLabel(t)}
                      onClick={() => requestToggle(t)}
                      busy={busyTask === t.id}
                      testId={`collab-toggle-${t.id}`}
                      trailing={<div className="text-[10px] uppercase tracking-wider text-dash-faint">{PHASE_LABELS[t.phase] || t.phase}</div>}
                    />
                  ))}
                </div>
              </div>
            )}
            {myTasks.length === 0 && (
              <div>
                <div className="text-[12px] font-bold uppercase tracking-[1px] text-dash-muted mb-2">My responsibilities</div>
                <EmptyCard icon={ClipboardCheck} text="Nothing assigned to you yet." testId="collab-my-tasks" />
              </div>
            )}

            {/* The Journey — orientation first, then the full read-only detail
                below it. Same timeline the client portal reads, in a neutral
                voice (`voice="external"`) since these sentences were written
                as the agent speaking to the client specifically. */}
            <Journey
              deal={deal}
              upcoming={journeyUpcoming}
              completed={journeyCompleted}
              voice="external"
              testId="collab-journey"
            />

            {/* Other tasks shared with me, read-only */}
            {otherTasks.length > 0 && (
              <div>
                <div className="text-[12px] font-bold uppercase tracking-[1px] text-dash-muted mb-2">Around the deal (read-only)</div>
                <div className="bg-white border border-dash-border rounded-2xl p-4 flex flex-col gap-3" data-testid="collab-other-tasks">
                  {otherTasks.slice(0, 8).map((t) => (
                    <ItemRow
                      key={t.id}
                      complete={t.status === TaskStatus.COMPLETE}
                      title={taskLabel(t)}
                      trailing={<RoleTagPill role={t.assigned_role} side={t.side || deal.deal_type} hostDealType={deal.deal_type} showSide={Boolean(deal.dual_sided)} />}
                    />
                  ))}
                </div>
              </div>
            )}

            <UpcomingAppointmentsCard dealId={deal.id} testId="collab-appointments" />
            <PortalDocumentsCard docs={docs} onOpen={downloadDoc} testId="collab-docs" />
          </section>

          {/* Right rail: agent + recent activity */}
          <aside className="flex min-w-0 flex-col gap-6 lg:sticky lg:top-5 lg:self-start" data-testid="collab-rail">
            <PortalCurrentChapter deal={deal} agent={deal.agent} testId="collab-current-chapter" />
            <PortalContactCard agent={deal.agent} roleLabel={agentRoleLabel} messageTo={`${SHARED_PROTECTED_ROUTES.CHATS}?deal=${id}`} testId="collab-agent-card" />
            <RecentActivityCard activity={activity} hostDealType={deal.deal_type} />
          </aside>
        </div>
      </div>

      <ConfirmCompleteDialog
        open={Boolean(confirmTask)}
        taskTitle={taskLabel(confirmTask)}
        onOpenChange={(open) => { if (!open) setConfirmTask(null); }}
        onConfirm={confirmCompletion}
      />
      {documentViewer}
    </DetailShell>
  );
}
